import { Button, ButtonStyles, Dropdown, Icons, Label, showAlert, TextInput, Tooltip } from '@silverfort/sf-components';
import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';
import { downloadFile } from '../../../utils';
import EntitiesDualSelectList, { TAB_OPTIONS, entity } from '../../../../sharedComponents/EntitiesDualSelectList';
import ChipList from '../../../common/ChipList';

export interface JITSettingsProps {
    adAttribute: string;
    adValue: string;
    accounts: entity[];
    selectedITSM?: string;
    availableITSMs: { title: string; value: string }[];
    updateADAttributes: (adAttribute: string, adValue: string) => void;
    updateSelectedAccounts: (accounts: entity[]) => void;
    updateDownloadScript: () => void;
    updateITSM: (oldItsm?: string, newItsm?: string) => void;
}

const JITSettings = ({
    adAttribute,
    adValue,
    accounts,
    selectedITSM = '',
    availableITSMs,
    updateADAttributes,
    updateSelectedAccounts,
    updateDownloadScript,
    updateITSM
}: JITSettingsProps) => {
    const [selectedAccounts, setSelectedAccounts] = useState<entity[]>(accounts);
    const [selectedGuids, setSelectedGuids] = useState<string[]>(accounts.map((account) => account.guid));
    const [adAttributeName, setAdAttributeName] = useState<string>(adAttribute);
    const [adAttributeValue, setAdAttributeValue] = useState<string>(adValue);
    const [itsm, setItsm] = useState<string>(selectedITSM);
    const isFirstRender = useRef(true);

    const sectionClassName = 'flex flex-col p-6 border border-gray-200 b-2 rounded-md gap-4';
    const labelClassName = 'text-md text-gray-700';
    const subtitleLabelClassName = labelClassName + ' min-h-12';
    const fieldLabelClassName = labelClassName + ' h-4';
    const descriptionLabelClassName = 'text-gray-400 font-normal';

    const changeITSM = (value?: string | string[]) => {
        if (Array.isArray(value)) {
            value = value[0];
        }
        const oldItsm = itsm;
        setItsm(value || '');
        updateITSM(oldItsm, value || '');
    };

    const isITSMAvailable = useMemo(() => {
        return availableITSMs.length > 1;
    }, [availableITSMs]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (selectedAccounts === undefined) return;
        updateSelectedAccounts(selectedAccounts);
        setSelectedGuids(selectedAccounts.map((account) => account.guid));
    }, [selectedAccounts]);

    const downloadGlassScript = () => {
        try {
            downloadFile('/privilegedAccounts/downloadBreakGlass/');
            updateDownloadScript();
        } catch (error) {
            showAlert('Failed to download break glass script', 'error');
        }
    };

    const removeItem = (guid: string) => {
        setSelectedAccounts((prev) => prev && prev.filter((account) => 
            account.guid !== guid
        ));
    };

    const applyButton = useMemo(() => {
        const applyButton = (
            <Button
                className="self-end"
                buttonStyle={ButtonStyles.Primary}
                text="Apply"
                onClick={() => updateADAttributes(adAttributeName, adAttributeValue)}
                disabled={!adAttributeName || !adAttributeValue}
            />
        );

        if (adAttributeName && adAttributeValue) {
            return applyButton;
        }
        return (
            <Tooltip className="flex" title="Both attribute and value are required to apply this setting">
                {applyButton}
            </Tooltip>
        );
    }, [adAttributeName, adAttributeValue, updateADAttributes]);

    const downloadScriptButton = useMemo(() => {
        const downloadScriptButton = (
            <Button
                buttonStyle={ButtonStyles.Primary}
                iconLeft={Icons.Download}
                text="Download"
                onClick={downloadGlassScript}
                disabled={!adAttribute || !adValue}
            />
        );
        if (adAttribute && adValue) {
            return downloadScriptButton;
        }
        return (
            <Tooltip className="flex" title="Both attribute and value are required to apply this setting">
                {downloadScriptButton}
            </Tooltip>
        );
    }, [adAttribute, adValue]);

    const buttonProps = { text: 'Add', buttonStyle: ButtonStyles.Primary } as Omit<
        HTMLAttributes<HTMLElement>,
        'onChange'
    >;

    return (
        <div className="flex flex-col">
            <div>
                <Label text="Active Directory attribute" className={subtitleLabelClassName} />
                <div className={sectionClassName}>
                    <Label
                        text="Define the attribute to indicate an account in Active Directory has an active JIT Policy."
                        className={descriptionLabelClassName}
                    />
                    <div className="flex gap-4">
                        <div className="flex flex-col gap-2">
                            <Label text="Attribute" className={fieldLabelClassName} />
                            <TextInput
                                className="max-h-8"
                                onChange={setAdAttributeName}
                                placeholder="description"
                                value={adAttributeName}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label text="Value" className={fieldLabelClassName} />
                            <TextInput
                                className="max-h-8"
                                onChange={setAdAttributeValue}
                                placeholder="Managed by Silverfort"
                                value={adAttributeValue}
                            />
                        </div>
                        {applyButton}
                    </div>
                </div>
            </div>
            <div>
                <Label text="Break glass script" className={subtitleLabelClassName} />
                <div className={sectionClassName}>
                    <Label
                        text="Download and execute the script to revert disabled accounts with the selected AD attribute, in case of a break glass scenario."
                        className={descriptionLabelClassName}
                    />
                    <div className="flex"> {downloadScriptButton}</div>
                </div>
            </div>
            <div>
                <Label text="Break glass accounts" className={subtitleLabelClassName} />
                <div className={sectionClassName}>
                    <span>
                        <Label
                            className={descriptionLabelClassName}
                            text="Select accounts to exclude from JIT enforcement to ensure continuous access."
                        />
                        <Label
                            className={descriptionLabelClassName}
                            text="JIT policies cannot be set until at least one break glass account is selected."
                        />
                    </span>
                    <EntitiesDualSelectList
                        selectedGuids={selectedGuids}
                        onClose={() => {}}
                        onSelect={(entities) => entities && setSelectedAccounts(entities)}
                        title={'Accounts'}
                        TriggerComponent={Button}
                        triggerProps={buttonProps}
                        entitiesTabsOptions={[TAB_OPTIONS.USERS]}
                    />
                    <ChipList
                        tableHeight="h-40"
                        listClassName="w-2/5"
                        onRemoveAll={() => setSelectedAccounts([])}
                        onRemove={removeItem}
                        data={selectedAccounts?.map((account) => ({
                            id: account.guid,
                            label: `${account.name} (${account.domain_name})`
                        })) || []}
                        listTitle="Accounts"
                        icon={Icons.UserGroup}
                        clearButtonClassName="font-normal"
                        clearButtonText="Clear all"
                    />
                </div>
            </div>
            <div>
                <Label text="Approval Provider" className={subtitleLabelClassName} />
                <div className={sectionClassName}>
                    <Label
                        text="Choose a ticketing system to approve privileged sessions in advance, if required by the policy."
                        className={descriptionLabelClassName}
                    />
                    <Dropdown
                        variant="trigger"
                        text="None"
                        items={availableITSMs}
                        selectedItems={itsm}
                        onChange={changeITSM}
                        disabled={!isITSMAvailable}
                    />
                </div>
            </div>
        </div>
    );
};

export default JITSettings;
